import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { useState } from "react"

import { ApiKeyTable } from "@/components/api-keys/api-key-table"
import { CreateApiKeyDialog } from "@/components/api-keys/create-api-key-dialog"
import { RevokeApiKeyDialog } from "@/components/api-keys/revoke-api-key-dialog"
import { OrganizationGuard } from "@/components/organization-guard"
import { TableSkeleton } from "@/components/skeletons/table-skeleton"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { listApiKeys } from "@/http/api-keys"
import type { TApiKey, TListApiKeysResponse } from "@/http/api-keys/types"
import { useOrganization } from "@/store/organization"

const apiKeysQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["api-keys", orgSlug],
    queryFn: async () => {
      const response = await listApiKeys({
        organizationSlug: orgSlug,
      })

      if (response.error) {
        throw new Error(response.error.message)
      }

      return response.data
    },
  })

/**
 * API Keys route - displays organization API keys and allows management
 */
export const Route = createFileRoute(
  "/_authenticated/$orgSlug/settings/api-keys",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      apiKeysQueryOptions(params.orgSlug),
    )
    return {
      title: "API Keys",
      path: `/${params.orgSlug}/settings/api-keys`,
    }
  },
  component: ApiKeysPage,
  pendingComponent: TableSkeleton,
})

function ApiKeysPage() {
  const { currentOrg } = useOrganization()
  const { orgSlug } = Route.useParams()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [revokeDialog, setRevokeDialog] = useState<{
    open: boolean
    apiKey: TApiKey | null
  }>({
    open: false,
    apiKey: null,
  })

  // Fetch API keys data
  const { data, refetch } = useSuspenseQuery(apiKeysQueryOptions(orgSlug))

  const apiKeys: TListApiKeysResponse["apiKeys"] = data?.apiKeys ?? []

  const handleRevoke = (apiKeyId: string) => {
    const apiKey = apiKeys.find((key) => key.id === apiKeyId)
    if (apiKey) {
      setRevokeDialog({ open: true, apiKey })
    }
  }

  const handleCreateSuccess = () => {
    refetch()
  }

  const handleRevokeSuccess = () => {
    refetch()
  }

  return (
    <OrganizationGuard action="manage" subject="ApiKey">
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold md:text-3xl">API Keys</h1>
          <p className="text-muted-foreground">
            Gerenciar as chaves de API da sua organização
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Criar chave de API
          </Button>
        </div>

        {/* Empty state */}
        {apiKeys.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                Nenhuma chave de API cadastrada
              </p>
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Crie sua primeira chave
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        {apiKeys.length > 0 && (
          <ApiKeyTable apiKeys={apiKeys} onRevoke={handleRevoke} />
        )}

        {/* Dialogs */}
        <CreateApiKeyDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          organizationSlug={currentOrg?.slug ?? ""}
          onSuccess={handleCreateSuccess}
        />

        <RevokeApiKeyDialog
          open={revokeDialog.open}
          onOpenChange={(open) =>
            setRevokeDialog({ open, apiKey: open ? revokeDialog.apiKey : null })
          }
          apiKey={revokeDialog.apiKey}
          organizationSlug={currentOrg?.slug ?? ""}
          onSuccess={handleRevokeSuccess}
        />
      </div>
    </OrganizationGuard>
  )
}
